// src/components/auth/AuthSidebar.jsx
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FiUser, FiLogOut, FiSettings, FiShield, FiLogIn } from 'react-icons/fi';
import LoginForm from './LoginForm';
import UserProfile from './UserProfile';

const AuthSidebar = () => {
  const { currentUser, userProfile, userRole, logout, isFirebaseAvailable } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [profileInitialTab, setProfileInitialTab] = useState('overview');
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLogout = async () => {
    setShowDropdown(false);
    await logout();
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'text-red-600 bg-red-100';
      case 'clinician': return 'text-purple-600 bg-purple-100';
      case 'staff': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return FiShield;
      case 'clinician': return FiUser;
      case 'staff': return FiUser;
      default: return FiUser;
    }
  };

  if (!isFirebaseAvailable) {
    return (
      <div className="p-4 bg-yellow-50 border-t border-yellow-200">
        <div className="flex items-center space-x-2 text-yellow-700">
          <FiShield className="w-4 h-4" />
          <span className="text-sm">Auth Unavailable</span>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <>
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={() => setShowLogin(true)}
            className="w-full flex items-center space-x-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiLogIn className="w-5 h-5" />
            <span>Sign In</span>
          </button>
        </div>

        {showLogin && (
          <LoginForm onClose={() => setShowLogin(false)} />
        )}
      </>
    );
  }

  const RoleIcon = getRoleIcon(userRole);

  return (
    <>
      <div className="p-4 border-t border-gray-200">
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <FiUser className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-gray-900 truncate">
                {userProfile?.firstName} {userProfile?.lastName}
              </p>
              <div className="flex items-center space-x-2">
                <RoleIcon className="w-3 h-3" />
                <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${getRoleColor(userRole)}`}>
                  {userRole}
                </span>
              </div>
            </div>
          </button>

          {showDropdown && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="p-2 space-y-1">
                <button
                  onClick={() => {
                    setProfileInitialTab('overview');
                    setShowProfile(true);
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiUser className="w-4 h-4" />
                  <span>My Profile</span>
                </button>
                
                <button
                  onClick={() => {
                    setProfileInitialTab('settings');
                    setShowProfile(true);
                    setShowDropdown(false);
                  }}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FiSettings className="w-4 h-4" />
                  <span>Settings</span>
                </button>
                
                <div className="border-t border-gray-200 my-1"></div>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <FiLogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Password Change Reminder */}
        {userProfile?.mustChangePassword && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center space-x-2 text-yellow-700">
              <FiShield className="w-4 h-4" />
              <span className="text-sm font-medium">Password Change Required</span>
            </div>
            <p className="text-xs text-yellow-600 mt-1">
              Please update your password in your profile settings.
            </p>
          </div>
        )}

        {/* GDPR/PDPA Consent Reminder */}
        {(!userProfile?.gdprConsent?.given || !userProfile?.pdpaConsent?.given) && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center space-x-2 text-blue-700">
              <FiShield className="w-4 h-4" />
              <span className="text-sm font-medium">Consent Required</span>
            </div>
            <p className="text-xs text-blue-600 mt-1">
              Please review and provide data processing consent in your profile.
            </p>
          </div>
        )}
      </div>

      {showProfile && (
        <UserProfile 
          onClose={() => setShowProfile(false)} 
          initialTab={profileInitialTab}
        />
      )}
    </>
  );
};

export default AuthSidebar;
